'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useQuery } from 'convex/react'
import { Clock, X } from 'lucide-react'
import { api } from '../../../convex/_generated/api'
import { Button } from '@/design-system'
import OnboardingCheckoutModal from '@/components/onboarding/OnboardingCheckoutModal'

const SESSION_STORAGE_KEY = 'trialBannerDismissed'

/**
 * Persistent top banner shown while the org is inside its active 48h
 * self-serve trial (not paid, not exempt). Modeled on `DesktopRecommendedBanner`
 * (fixed top strip, dismiss button, `role="status"` / `aria-live`) but
 * deliberately does NOT reuse its `localStorage` dismissal: a hard 48h payment
 * cutoff must never be permanently dismissible, so dismissal here is
 * per-session only (`sessionStorage`, cleared on browser/tab close) rather
 * than persisted across visits.
 *
 * Shows a coarse time-remaining bucket ("about 6 hours left") instead of a
 * live ticking countdown. A minute-by-minute countdown would (a) require
 * client-only state that can mismatch the server-rendered markup on first
 * paint, and (b) force unnecessary re-renders for a message that doesn't
 * need second-level precision — the whole point is to nudge the user to add
 * payment, not to function as a clock.
 */
export default function TrialBanner() {
  const t = useTranslations('billing.trialBanner')
  const orgStatus = useQuery(api.organizations.getCurrentOrgStatus, {})
  const [dismissed, setDismissed] = useState(true)
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false)

  useEffect(() => {
    const stored = typeof window !== 'undefined' ? sessionStorage.getItem(SESSION_STORAGE_KEY) : null
    setDismissed(stored === '1')
  }, [])

  // Guard on the loading state (orgStatus === undefined) matching
  // ConditionalLayout's own loading convention, and on the state discriminant
  // — only the "active" branch carries a `trial` descriptor at all. Any other
  // state (unauthenticated / no_org / inactive), or an "active" org whose
  // `trial` is otherwise absent/undefined-safe-falsy, must not crash the banner.
  if (orgStatus === undefined) return null
  if (orgStatus.state !== 'active' || !orgStatus.trial) return null

  const { trial } = orgStatus
  if (!trial.active || trial.endDate === null) return null
  if (dismissed) return null

  const msRemaining = trial.endDate - Date.now()
  if (msRemaining <= 0) return null

  const hoursRemaining = Math.ceil(msRemaining / (1000 * 60 * 60))

  // Coarse buckets rather than a live ticking countdown (Frontend Review §2):
  // recomputed on mount/re-render, never a per-second/per-minute timer.
  const timeLabel =
    hoursRemaining <= 1
      ? t('lessThanOneHour')
      : hoursRemaining <= 24
        ? t('aboutHours', { hours: hoursRemaining })
        : t('aboutOneDay')

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, '1')
    } catch {
      // sessionStorage unavailable (private mode, etc.) — dismiss for this render only.
    }
    setDismissed(true)
  }

  return (
    <>
      <div
        className="flex flex-wrap items-center justify-center gap-3 px-4 py-2.5 text-sm relative"
        style={{
          background: 'var(--color-lime-muted)',
          borderBottom: '1px solid var(--color-border)',
          color: 'var(--color-text-secondary)',
        }}
        role="status"
        aria-live="polite"
      >
        <Clock className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--color-lime)' }} />
        <span className="text-center">
          <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {t('heading', { time: timeLabel })}
          </span>{' '}
          {t('body')}
        </span>
        <Button
          variant="default"
          size="sm"
          className="shrink-0"
          onClick={() => setIsCheckoutOpen(true)}
        >
          {t('addPaymentButton')}
        </Button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('dismiss')}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:opacity-70 transition-opacity"
          style={{ color: 'var(--color-text-muted)' }}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <OnboardingCheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        onComplete={() => setIsCheckoutOpen(false)}
      />
    </>
  )
}
